/**
 * VORE — comportamento do rodape (vore-footer.js)
 *
 * Dois custom elements, ambos com degradacao graciosa (sem JS o HTML continua
 * utilizavel):
 *
 *   <vore-footer-icons>  faixa de garantias. No mobile o track e um carrossel
 *                       scroll-snap; este elemento sincroniza os dots com a
 *                       rolagem e move o track quando um dot e acionado.
 *                       Sem JS: o carrossel continua rolando com o dedo, so os
 *                       dots ficam inertes (por isso ficam ocultos ate o JS
 *                       marcar o elemento como pronto).
 *
 *   <vore-footer-menus>  colunas de links do rodape. O HTML sai com todos os
 *                       <details open>, entao sem JS os links ficam todos
 *                       visiveis. Com JS: abaixo de 1024px viram acordeoes
 *                       fechados (comportamento do site original) e acima de
 *                       1024px ficam abertos e travados, funcionando como
 *                       listas simples.
 *
 * O site original faz as duas coisas com jQuery/JS inline por secao e usa
 * <div> nao focavel como dot. Ver VORE-DIVERGENCIAS.md.
 */

const DESKTOP_QUERY = '(min-width: 1024px)';

class RstFooterIcons extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-vore-track]');
    this.dots = Array.from(this.querySelectorAll('[data-vore-dot]'));

    if (!this.track || this.dots.length === 0) return;

    this.index = 0;
    this.onScroll = this.onScroll.bind(this);
    this.onDotClick = this.onDotClick.bind(this);

    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    this.dots.forEach((dot) => dot.addEventListener('click', this.onDotClick));

    this.setAttribute('data-vore-ready', 'true');
    this.updateDots(0);
  }

  disconnectedCallback() {
    this.track?.removeEventListener('scroll', this.onScroll);
    this.dots?.forEach((dot) => dot.removeEventListener('click', this.onDotClick));
  }

  onDotClick(event) {
    const dot = event.currentTarget;
    const index = Number(dot.dataset.rstDot || 0);
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.track.scrollTo({
      left: this.track.clientWidth * index,
      behavior: reduceMotion ? 'auto' : 'smooth',
    });

    this.updateDots(index);
  }

  onScroll() {
    if (this.track.clientWidth === 0) return;

    const index = Math.round(this.track.scrollLeft / this.track.clientWidth);
    if (index !== this.index) this.updateDots(index);
  }

  updateDots(index) {
    this.index = index;

    this.dots.forEach((dot, position) => {
      const current = position === index;
      dot.setAttribute('aria-current', current ? 'true' : 'false');
    });
  }
}

class RstFooterMenus extends HTMLElement {
  connectedCallback() {
    this.panels = Array.from(this.querySelectorAll('details.vore-footer__col'));
    if (this.panels.length === 0) return;

    this.media = window.matchMedia(DESKTOP_QUERY);
    this.sync = this.sync.bind(this);
    this.blockToggleOnDesktop = this.blockToggleOnDesktop.bind(this);

    this.panels.forEach((panel) => {
      const summary = panel.querySelector('summary');
      summary?.addEventListener('click', this.blockToggleOnDesktop);
    });

    // `addEventListener` em MediaQueryList: Safari < 14 so tem addListener.
    if (typeof this.media.addEventListener === 'function') {
      this.media.addEventListener('change', this.sync);
    } else if (typeof this.media.addListener === 'function') {
      this.media.addListener(this.sync);
    }

    this.sync();
  }

  disconnectedCallback() {
    if (!this.media) return;

    if (typeof this.media.removeEventListener === 'function') {
      this.media.removeEventListener('change', this.sync);
    } else if (typeof this.media.removeListener === 'function') {
      this.media.removeListener(this.sync);
    }
  }

  blockToggleOnDesktop(event) {
    if (this.media.matches) event.preventDefault();
  }

  sync() {
    const isDesktop = this.media.matches;

    this.panels.forEach((panel) => {
      panel.open = isDesktop;

      const summary = panel.querySelector('summary');
      if (!summary) return;

      // No desktop a coluna nao e mais um disclosure: e so um titulo.
      if (isDesktop) {
        summary.setAttribute('aria-disabled', 'true');
        summary.setAttribute('tabindex', '-1');
      } else {
        summary.removeAttribute('aria-disabled');
        summary.removeAttribute('tabindex');
      }
    });
  }
}

if (!customElements.get('vore-footer-icons')) {
  customElements.define('vore-footer-icons', RstFooterIcons);
}

if (!customElements.get('vore-footer-menus')) {
  customElements.define('vore-footer-menus', RstFooterMenus);
}
